import { useNotificationText } from './useNotificationText';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useRef, useCallback } from 'react';
import type { KeyboardEvent } from 'react';
import type { Notification } from '../../types/notification.types';
import {
  X,
  Bell,
  CheckCircle,
  XCircle,
  Calendar,
  AlertCircle,
} from 'lucide-react';

interface NotificationToastProps {
  notification: Notification;
  onClose: () => void;
  /** Marks it read and opens its page. Absent for plain app toasts, which
   *  reuse this card and open nothing. */
  onOpen?: () => void;
  duration?: number;
}

const getNotificationIcon = (type: string) => {
  switch (type) {
    case 'appointment_request':
      return <Calendar className="w-5 h-5 text-info-500" />;
    case 'appointment_approved':
      return <CheckCircle className="w-5 h-5 text-success-500" />;
    case 'appointment_rejected':
      return <XCircle className="w-5 h-5 text-danger-500" />;
    case 'appointment_reminder':
      return <Bell className="w-5 h-5 text-warning-500" />;
    default:
      return <AlertCircle className="w-5 h-5 text-info-500" />;
  }
};

export const NotificationToast = ({
  notification,
  onClose,
  onOpen,
  duration = 5000,
}: NotificationToastProps) => {
  const { t } = useTranslation();
  const text = useNotificationText()(notification);
  const [isExiting, setIsExiting] = useState(false);
  const mainTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const startExitAnimation = useCallback(() => {
    setIsExiting(true);
    if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    exitTimerRef.current = setTimeout(onClose, 300); // Wait for exit animation
  }, [onClose]);

  useEffect(() => {
    mainTimerRef.current = setTimeout(startExitAnimation, duration);

    return () => {
      if (mainTimerRef.current) clearTimeout(mainTimerRef.current);
      if (exitTimerRef.current) clearTimeout(exitTimerRef.current);
    };
  }, [duration, startExitAnimation]);

  const handleClose = () => {
    if (mainTimerRef.current) clearTimeout(mainTimerRef.current);
    startExitAnimation();
  };

  return (
    <div
      {...(onOpen && {
        role: 'button',
        tabIndex: 0,
        onClick: onOpen,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpen();
          }
        },
      })}
      className={`
        ${onOpen ? 'cursor-pointer' : ''} bg-white rounded-lg shadow-2xl border border-neutral-200 p-4 
        min-w-[320px] max-w-[400px] mb-4
        transition-all duration-300 ease-in-out
        ${
          isExiting
            ? 'translate-x-[120%] opacity-0'
            : 'translate-x-0 opacity-100'
        }
      `}
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {getNotificationIcon(notification.type)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold text-neutral-800">
              {text.title}
            </h4>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className="flex-shrink-0 text-neutral-400 hover:text-neutral-600 
                       transition-colors p-0.5 rounded hover:bg-neutral-100"
              aria-label={t('common.close')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="text-sm text-neutral-600 mt-1 line-clamp-2">
            {text.message}
          </p>
        </div>
      </div>
    </div>
  );
};
